import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onStart }) {
  const [falling, setFalling] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 35, damping: 18 });
  const smy = useSpring(my, { stiffness: 35, damping: 18 });
  const treeX = useTransform(smx, [-1, 1], [12, -12]);
  const treeY = useTransform(smy, [-1, 1], [8, -8]);

  const start = () => {
    if (falling) return;
    setFalling(true);
    window.setTimeout(onStart, 1550);
  };

  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  return (
    <motion.section
      data-testid="hero-intro"
      onMouseMove={(e) => {
        mx.set((e.clientX / window.innerWidth) * 2 - 1);
        my.set((e.clientY / window.innerHeight) * 2 - 1);
      }}
      className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-white"
      exit={{ opacity: 0, scale: 1.04, filter: "blur(8px)" }}
      transition={{ duration: 1, ease: EASE }}
    >
      <div className="pointer-events-none absolute inset-4 border border-[#D4AF37]/20 sm:inset-6" />

      <motion.div
        initial={{ opacity: 0, y: -70 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.5, delay: 0.4, ease: EASE }}
        className="absolute inset-x-0 top-0 w-full"
      >
        <motion.div style={{ x: treeX, y: treeY }} className="relative">
          <img
            src="/crown.png"
            alt="Tree crown with golden apples"
            data-testid="hero-crown"
            className="w-full"
          />
          <motion.img
            src="/apple.png"
            alt="Golden apple"
            data-testid="hero-apple"
            className="absolute w-[6.2%]"
            style={{ left: "42.1%", top: "26.7%" }}
            animate={
              falling
                ? {
                    x: ["0vw", "36vw"],
                    y: [0, vh * 0.34, vh * 0.29, vh * 0.52, vh * 0.47, vh * 1.05],
                    rotate: [0, 220],
                  }
                : { y: [0, -4, 0], rotate: [-2, 2, -2] }
            }
            transition={
              falling
                ? {
                    x: { duration: 1.1, ease: "easeOut" },
                    y: { duration: 1.5, times: [0, 0.38, 0.5, 0.72, 0.82, 1], ease: "easeIn" },
                    rotate: { duration: 1.5, ease: "easeIn" },
                  }
                : { duration: 4.5, repeat: Infinity, ease: "easeInOut" }
            }
          />
        </motion.div>
      </motion.div>

      <header
        className="absolute inset-x-0 z-10 px-6 text-center"
        style={{ top: "max(29vw, 50vh)" }}
      >
        <h1 className="sr-only">SYPAMA Holding GmbH</h1>
        <span className="block overflow-hidden">
          <motion.img
            src="/logo-gold.png"
            alt="SYPAMA Holding GmbH"
            data-testid="hero-logo"
            className="mx-auto block w-[min(78vw,660px)]"
            initial={{ y: "70%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
          />
        </span>
        <MaskLine delay={0.85}>
          <span className="f-mono mt-6 block text-[10px] tracking-[0.45em] text-slate-600 sm:text-xs">
            INVESTING IN LASTING VALUE.
          </span>
        </MaskLine>
      </header>

      <motion.div
        className="absolute inset-x-0 bottom-[5vh] z-10 flex justify-center"
        initial={{ opacity: 0, y: 24 }}
        animate={falling ? { opacity: 0, y: 12 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: falling ? 0 : 1.7, ease: EASE }}
      >
        <button
          data-testid="start-button"
          onClick={start}
          className="f-mono group flex items-center gap-3 rounded-full border border-[#D4AF37]/60 bg-white/80 px-8 py-4 text-[11px] tracking-[0.4em] text-slate-900 backdrop-blur-md transition-colors duration-500 hover:bg-[#D4AF37] hover:text-white"
        >
          START
          <ArrowDown className="h-4 w-4 transition-transform duration-500 group-hover:translate-y-1" />
        </button>
      </motion.div>
    </motion.section>
  );
}
